import { useState, useEffect } from "react";
import { isStaffEmail } from "@utils/user_utils";
import { TrainingAPI } from "../../../api/TrainingAPI";
import { Space } from "antd";
import { audioMessage as message } from "../../Common/Notifications/audioMessage";
import { useDispatch, useSelector } from "react-redux";
import { getColorStrategies, updateDifficultyBarConfig, updateGymMetricColorLogic, getTeamInfo, validateTeamAndNotify } from "../../../store/actions/team.actions";
import BlurredCustomColorStrategies from "./blurred_custom_color_strategy.png";
import { PremiumFeatureDisabled } from "../../Common/PremiumFeatureDisabled";
import { DifficultyBarConfig } from "./DifficultyBarConfig";
import { ColorStrategyModal } from "./ColorStrategyModal";
import { ColorStrategyManagement } from "./ColorStrategyManagement";
import { GymMetricColorGroupings } from "./GymMetricColorGroupings";
import { AdminNotice } from "../AdminNotice";
import { useAuth0 } from "@auth0/auth0-react";
import "../../../Styles/styles-CustomColorStrategies.css"

export const CustomColorStrategies = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState(null);
  const trainingAPI = new TrainingAPI();
  const dispatch = useDispatch();

  const {
    color_strategies: strategies,
    info: { custom_colors_enabled, gym_metric_color_logic: gymMetricColorLogic  },
    misc: { color_strategy_variables: strategyVariables = [] }
  } = useSelector(({ team }) => team);

  const currentUserId = useSelector(({ auth }) => auth.currentUser.user_id);

  const { user } = useAuth0();
  const isStaff = isStaffEmail(user?.email);

  useEffect(() => {
    dispatch(getColorStrategies());
    dispatch(getTeamInfo());
  }, [dispatch]);

  const handleModalSubmit = async (strategyData, editingStrategy) => {
    try {
      if (editingStrategy) {
        await trainingAPI.updateColorStrategy({
          ...strategyData,
          strategy_id: editingStrategy.id
        });
        message.success("Strategy updated successfully");
      } else {
        await trainingAPI.createColorStrategy(strategyData);
        message.success("Strategy created successfully");
      }
      setIsModalVisible(false);
      setEditingStrategy(null);
      dispatch(getColorStrategies());
    } catch (error) {
      message.error(typeof error === 'string' ? error : "Error saving color strategy");
    }

    dispatch(validateTeamAndNotify());
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingStrategy(null);
  };

  const handleDelete = async (selectedStrategy) => {
    try {
      await trainingAPI.deleteColorStrategy(selectedStrategy.id);
      message.success("Strategy deleted successfully");
      dispatch(getColorStrategies());
    } catch (error) {
      message.error(typeof error === 'string' ? error : "Error deleting color strategy");
    }

    dispatch(validateTeamAndNotify());
  };

  const handleEdit = (selectedStrategy) => {
    setEditingStrategy(selectedStrategy);
    setIsModalVisible(true);
  };

  const handleAdd = () => {
    setEditingStrategy(null);
    setIsModalVisible(true);
  };

  const handleDifficultyBarSave = async (config) => {
    try {
      await dispatch(updateDifficultyBarConfig({ difficulty_bar_config: config }));
      message.success("Difficulty bar configuration saved successfully");
      dispatch(validateTeamAndNotify());
    } catch (error) {
      message.error(typeof error === 'string' ? error : "Error saving difficulty bar configuration");
    }
  };

  const handleGymMetricColorLogicSave = async (data) => {
    try {
      await dispatch(updateGymMetricColorLogic(data));
      message.success("Forceplate/testing metric color logic saved successfully");
      dispatch(validateTeamAndNotify());
    } catch (error) {
      message.error(typeof error === 'string' ? error : "Error saving forceplate/testing color logic");
    }
  };

  if (!custom_colors_enabled && !isStaff) {
    return (
      <PremiumFeatureDisabled
        bg_image={BlurredCustomColorStrategies}
        show_navbar={false}
        feature_name={"Custom Coloring"}
        backgroundPosition={'unset'}
        info_text={<>
        This is a premium feature that will allow you to customize the colors of your GPS reports. <br></br>
        If you are interested in adding this to your platform, please contact your sales rep!</>}
      />
    )
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {
          (!custom_colors_enabled && isStaff) ? 
            <AdminNotice />
           : null
        }
        {/* Color Strategy Management Section */}
        <ColorStrategyManagement
          strategies={strategies}
          currentUserId={currentUserId}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />

        {/* Difficulty Bar Configuration Section */}
        <DifficultyBarConfig
          onSave={handleDifficultyBarSave}
        />

        {/* Gym Metric Color Groupings Section */}
        <GymMetricColorGroupings
          onSave={handleGymMetricColorLogicSave}
        />

        <ColorStrategyModal
          isVisible={isModalVisible}
          onCancel={handleModalCancel}
          onSubmit={handleModalSubmit}
          editingStrategy={editingStrategy}
          currentUserId={currentUserId}
          strategyVariables={strategyVariables}
        />
      </Space>
    </div>
  );
}; 